import ReactDOM from 'react-dom/client';
import { setChonkyDefaults } from 'chonky';
import { ChonkyIconFA } from 'chonky-icon-fontawesome';
import { DndProvider, DndProviderProps } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import App from './App.tsx';
import { ConfigProvider } from 'antd';

type ExtendedDndProviderProps = DndProviderProps<any, any> & {
  children?: React.ReactNode;
};

// Perf: every query was previously stale-the-moment-it-finished, so a remount
// re-fetched even when the data was seconds old. A modest staleTime caches
// across remounts/route changes; refetchOnWindowFocus off avoids a refetch
// every time the user tab-switches; one retry is enough — we don't want long
// retry-storms on a degraded network.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const ExtendedDndProvider = DndProvider as React.FC<ExtendedDndProviderProps>;

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <ConfigProvider
    theme={{
      hashed: false,
      components: {
        // Brand-teal sliders with a thicker rail + a white handle that has a
        // teal ring. Driven by AntD's own tokens so the handle stays perfectly
        // centred on the track (CSS height hacks break that centring).
        Slider: {
          railSize: 8,
          handleSize: 10,
          handleSizeHover: 10,
          handleLineWidth: 1,
          handleLineWidthHover: 1,
          railBg: '#e5e7eb',
          railHoverBg: '#d9dde2',
          trackBg: '#007D8A',
          trackHoverBg: '#007D8A',
          handleColor: '#007D8A',
          handleActiveColor: '#007D8A',
        },
      },
    }}
  >
    <QueryClientProvider client={queryClient}>
      <ExtendedDndProvider backend={HTML5Backend}>
        <App />
      </ExtendedDndProvider>
    </QueryClientProvider>
  </ConfigProvider>,
);

setChonkyDefaults({ iconComponent: ChonkyIconFA } as any);
